import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Close,
  CloudUpload,
  Delete,
  Inventory,
  MiscellaneousServices,
  PictureAsPdf,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { ServiceProduct, ServiceType, CreateServiceProductRequest, UpdateServiceProductRequest } from '@/constants/types';
import { serviceProductService } from '@/services/business/serviceProductService';
import { uploadService } from '@/services/upload/uploadService';

const IMAGE_RECOMMENDED = { width: 1280, height: 720 };

const readImageDimensions = (url: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Failed to read image'));
    img.src = url;
  });

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 2 : 1)} MB`;
};

interface AddEditServiceProductModalProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  editProduct?: ServiceProduct | null;
  onSuccess: () => void;
}

const validationSchema = Yup.object({
  serviceType: Yup.string().oneOf(['product', 'service']).required('Type is required'),
  title: Yup.string().max(150, 'Title must be at most 150 characters').required('Title is required'),
  price: Yup.number().min(0, 'Price must be positive').nullable(),
  description: Yup.string().test(
    'word-count',
    'Description must be at most 200 words',
    (value) => !value || value.split(/\s+/).filter(Boolean).length <= 200
  ),
});

const wordCount = (text: string) => (text ? text.split(/\s+/).filter(Boolean).length : 0);

export const AddEditServiceProductModal: React.FC<AddEditServiceProductModalProps> = ({
  open,
  onClose,
  businessId,
  editProduct,
  onSuccess,
}) => {
  const theme = useTheme();
  const isEditing = !!editProduct;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productImage, setProductImage] = useState<{ mediaFileId?: string; url?: string; fileName?: string; size?: number } | null>(null);
  const [brochure, setBrochure] = useState<{ mediaFileId?: string; url?: string; fileName?: string; size?: number } | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [brochureUploading, setBrochureUploading] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  const formik = useFormik({
    initialValues: {
      serviceType: (editProduct?.serviceType || 'product') as ServiceType,
      title: editProduct?.title || '',
      price: editProduct?.price?.toString() || '',
      description: editProduct?.description || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);

        const payload: CreateServiceProductRequest | UpdateServiceProductRequest = {
          serviceType: values.serviceType,
          title: values.title,
          price: values.price ? Number(values.price) : undefined,
          description: values.description || undefined,
          productImage: productImage || undefined,
          brochure: brochure || undefined,
        };

        if (isEditing && editProduct) {
          await serviceProductService.updateServiceProduct(editProduct._id, payload);
          toast.success('Updated successfully');
        } else {
          await serviceProductService.createServiceProduct({
            ...payload,
            businessId,
          } as CreateServiceProductRequest);
          toast.success('Created successfully');
        }

        onSuccess();
        handleClose();
      } catch (err: any) {
        toast.error(err?.message || 'Failed to save');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (editProduct) {
      setProductImage(editProduct.productImage || null);
      setBrochure(editProduct.brochure || null);
    } else {
      setProductImage(null);
      setBrochure(null);
    }
    setImageDimensions(null);
  }, [editProduct, open]);

  // Read dimensions whenever a product image URL becomes available
  useEffect(() => {
    let cancelled = false;
    const url = productImage?.url;
    if (!url) {
      setImageDimensions(null);
      return;
    }
    readImageDimensions(url)
      .then((dim) => { if (!cancelled) setImageDimensions(dim); })
      .catch(() => { if (!cancelled) setImageDimensions(null); });
    return () => { cancelled = true; };
  }, [productImage?.url]);

  const handleClose = () => {
    formik.resetForm();
    setProductImage(null);
    setBrochure(null);
    setImageDimensions(null);
    onClose();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    try {
      setImageUploading(true);
      const response = await uploadService.uploadSingle(file, { folder: 'service-products' });
      setProductImage({
        mediaFileId: response.file.id,
        url: response.file.url,
        fileName: response.file.fileName,
        size: file.size,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleBrochureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed for brochure');
      return;
    }

    try {
      setBrochureUploading(true);
      const response = await uploadService.uploadSingle(file, { folder: 'brochures' });
      setBrochure({
        mediaFileId: response.file.id,
        url: response.file.url,
        fileName: response.file.fileName,
        size: file.size,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload brochure');
    } finally {
      setBrochureUploading(false);
    }
  };

  const removeImage = () => {
    setProductImage(null);
    setImageDimensions(null);
  };
  const removeBrochure = () => setBrochure(null);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          {isEditing ? 'Edit' : 'Add'} {formik.values.serviceType === 'product' ? 'Product' : 'Service'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          {/* Type Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Type
            </Typography>
            <ToggleButtonGroup
              value={formik.values.serviceType}
              exclusive
              onChange={(_, value) => value && formik.setFieldValue('serviceType', value)}
              fullWidth
              size="small"
            >
              <ToggleButton value="product" sx={{ textTransform: 'none' }}>
                <Inventory sx={{ mr: 1, fontSize: 18 }} /> Product
              </ToggleButton>
              <ToggleButton value="service" sx={{ textTransform: 'none' }}>
                <MiscellaneousServices sx={{ mr: 1, fontSize: 18 }} /> Service
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Title */}
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.title && Boolean(formik.errors.title)}
            helperText={
              (formik.touched.title && formik.errors.title) ||
              `${formik.values.title.length}/150 characters`
            }
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 150 }}
          />

          {/* Price */}
          <TextField
            fullWidth
            label="Price (Optional)"
            name="price"
            type="number"
            value={formik.values.price}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.price && Boolean(formik.errors.price)}
            helperText={formik.touched.price && formik.errors.price}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>&#8377;</Typography>,
            }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={
              (formik.touched.description && formik.errors.description) ||
              `${wordCount(formik.values.description)}/200 words`
            }
            multiline
            rows={4}
            sx={{ mb: 3 }}
          />

          {/* Product Image */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle2">
                {formik.values.serviceType === 'product' ? 'Product' : 'Service'} Image
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Recommended: {IMAGE_RECOMMENDED.width}×{IMAGE_RECOMMENDED.height} (16:9)
              </Typography>
            </Box>
            {productImage?.url ? (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16 / 9',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                }}
              >
                <Box
                  component="img"
                  src={productImage.url}
                  alt="Product"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                <IconButton
                  onClick={removeImage}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
                {imageDimensions && (
                  <Chip
                    label={`${imageDimensions.width}×${imageDimensions.height}`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: 'rgba(0,0,0,0.65)',
                      color: 'white',
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box
                component="label"
                aria-disabled={imageUploading}
                sx={{
                  width: '100%',
                  aspectRatio: '16 / 9',
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 0.75,
                  px: 2,
                  cursor: imageUploading ? 'not-allowed' : 'pointer',
                  opacity: imageUploading ? 0.7 : 1,
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': imageUploading
                    ? undefined
                    : {
                        borderColor: 'primary.main',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {imageUploading ? <CircularProgress size={24} sx={{ color: 'inherit' }} /> : <CloudUpload sx={{ fontSize: 26 }} />}
                </Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  {imageUploading ? 'Uploading…' : 'Click to upload image'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  PNG, JPG · 16:9 · {IMAGE_RECOMMENDED.width}×{IMAGE_RECOMMENDED.height} recommended
                </Typography>
                <input type="file" hidden accept="image/*" onChange={handleImageUpload} disabled={imageUploading} />
              </Box>
            )}
          </Box>

          {/* Brochure (PDF) */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Brochure (PDF only)
            </Typography>
            {brochure?.url ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <PictureAsPdf color="error" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {brochure.fileName || 'Brochure.pdf'}
                    </Typography>
                    {formatFileSize(brochure.size) && (
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(brochure.size)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton onClick={removeBrochure} size="small" color="error">
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                component="label"
                aria-disabled={brochureUploading}
                sx={{
                  width: '100%',
                  py: 2.5,
                  px: 2,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  cursor: brochureUploading ? 'not-allowed' : 'pointer',
                  opacity: brochureUploading ? 0.7 : 1,
                  transition: 'border-color 0.2s, background-color 0.2s',
                  '&:hover': brochureUploading
                    ? undefined
                    : {
                        borderColor: 'primary.main',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                    color: 'common.white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {brochureUploading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <PictureAsPdf sx={{ fontSize: 20 }} />}
                </Box>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {brochureUploading ? 'Uploading…' : 'Click to upload brochure'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF only · max 10 MB
                  </Typography>
                </Box>
                <input type="file" hidden accept="application/pdf" onChange={handleBrochureUpload} disabled={brochureUploading} />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !formik.isValid}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
